import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Save, X, AlertCircle, CheckCircle, Loader2, Link2, UserPlus, Building2, FileText } from 'lucide-react';
import { toast } from '../ui/Toast';
import { patientsApi } from '../../api/patients';
import { insuranceCompaniesApi, type InsuranceCompanySelect } from '../../api/insurance-companies';
import { useDebounce } from '../../hooks/useDebounce';
import { useTrackUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import type { Patient, PatientFormData, DocumentType, ContactPreference, PatientCompanySearchResult } from '../../types/patient';
import { DOCUMENT_TYPE_LABELS, CONTACT_PREF_LABELS } from '../../types/patient';

interface PatientFormProps {
  initialData?: Patient;
  onSubmit: (data: PatientFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

type ValidationStatus = 'idle' | 'checking' | 'valid' | 'duplicate' | 'duplicate_other_site';

// Componente Toast flotante para vincular paciente
function LinkPatientToast({
  patient,
  onLink,
  onDismiss,
  isLinking,
  medicalRecordNumber,
  onMedicalRecordNumberChange
}: {
  patient: PatientCompanySearchResult;
  onLink: () => void;
  onDismiss: () => void;
  isLinking: boolean;
  medicalRecordNumber: string;
  onMedicalRecordNumberChange: (value: string) => void;
}) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDismissClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmDismiss = () => {
    setShowConfirm(false);
    onDismiss();
  };

  const handleCancelDismiss = () => {
    setShowConfirm(false);
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.2)] border-2 border-blue-400 p-5 w-[420px] max-w-[calc(100vw-2rem)]">
        {/* Barra superior de acento */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 rounded-t-2xl" />

        {/* Header con icono grande */}
        <div className="flex items-start gap-4 mt-1">
          <div className="flex-shrink-0">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <UserPlus className="h-7 w-7 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-lg font-bold text-gray-900">
                ¡Paciente encontrado!
              </h3>
              <button
                onClick={handleDismissClick}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Este paciente ya existe en otro centro de la empresa
            </p>
          </div>
        </div>

        {/* Info del paciente */}
        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
              {patient.fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">{patient.fullName}</p>
              <p className="text-sm text-gray-600">{patient.documentType}: {patient.documentNumber}</p>
            </div>
          </div>

          {/* Centros donde está vinculado */}
          {patient.linkedSites && patient.linkedSites.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <span className="text-xs text-gray-500">Vinculado en:</span>
              {patient.linkedSites.map((site) => (
                <span
                  key={site.siteId}
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    site.isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {site.siteName}
                  {site.medicalRecordNumber && ` (${site.medicalRecordNumber})`}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Input de número de historia */}
        <div className="mt-4 flex items-center gap-2 px-1">
          <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={medicalRecordNumber}
            onChange={(e) => onMedicalRecordNumberChange(e.target.value.toUpperCase())}
            placeholder="Nº Historia en este centro (opcional)"
            maxLength={50}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Diálogo de confirmación */}
        {showConfirm ? (
          <div className="mt-4 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  ¿Estás seguro de que quieres crear un paciente nuevo?
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Se borrará el DNI escrito y podrás empezar de nuevo.
                </p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleCancelDismiss}
                className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDismiss}
                className="flex-1 px-3 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
              >
                Sí, crear nuevo
              </button>
            </div>
          </div>
        ) : (
          /* Botón de vincular centrado */
          <div className="mt-4">
            <button
              type="button"
              onClick={onLink}
              disabled={isLinking}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
              className="w-full px-4 py-3 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 border-2 border-blue-700"
            >
              {isLinking ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Vinculando...</span>
                </>
              ) : (
                <>
                  <Link2 className="h-5 w-5" />
                  <span>Vincular a este centro</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PatientForm({ initialData, onSubmit, onCancel, isLoading = false }: PatientFormProps) {
  const navigate = useNavigate();
  const isEditing = !!initialData;

  // Estado para compañías de seguros
  const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompanySelect[]>([]);
  const [loadingInsurance, setLoadingInsurance] = useState(true);

  // Estado para paciente duplicado en otro centro
  const [duplicateInOtherSite, setDuplicateInOtherSite] = useState<PatientCompanySearchResult | null>(null);
  const [showLinkToast, setShowLinkToast] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [toastMedicalRecordNumber, setToastMedicalRecordNumber] = useState<string>('');

  // Cargar compañías de seguros
  useEffect(() => {
    const loadInsuranceCompanies = async () => {
      try {
        const data = await insuranceCompaniesApi.getForSelect();
        setInsuranceCompanies(data);
      } catch (error) {
        console.error('Error loading insurance companies:', error);
        toast.error('Error', 'No se pudieron cargar las compañías de seguros');
      } finally {
        setLoadingInsurance(false);
      }
    };
    loadInsuranceCompanies();
  }, []);

  const { register, handleSubmit, watch, setValue, formState: { errors, isDirty } } = useForm<PatientFormData>({
    defaultValues: initialData ? {
      firstName: initialData.firstName,
      lastName: initialData.lastName,
      documentType: initialData.documentType,
      documentNumber: initialData.documentNumber,
      birthDate: initialData.birthDate,
      phonePrimary: initialData.phonePrimary,
      email: initialData.email,
      contactPref: initialData.contactPref,
      insuranceCompanyId: initialData.insuranceCompanyId,
      medicalRecordNumber: initialData.medicalRecordNumber || '',
      phoneSecondary: initialData.phoneSecondary || '',
      addressStreet: initialData.addressStreet || '',
      addressNumber: initialData.addressNumber || '',
      addressFloor: initialData.addressFloor || '',
      postalCode: initialData.postalCode || '',
      city: initialData.city || '',
      province: initialData.province || '',
      country: initialData.country || 'España',
      adminNotes: initialData.adminNotes || '',
    } : {
      documentType: 'DNI' as DocumentType,
      contactPref: 'PHONE' as ContactPreference,
      insuranceCompanyId: '',
      medicalRecordNumber: '',
      country: 'España',
    },
  });

  // Trackear cambios sin guardar para el selector de centro
  useTrackUnsavedChanges(isDirty, isEditing ? "Edición de paciente" : "Nuevo paciente");

  // Validación de duplicados
  const documentNumber = watch('documentNumber');
  const email = watch('email');
  const debouncedDoc = useDebounce(documentNumber, 500);
  const debouncedEmail = useDebounce(email, 500);

  const [docStatus, setDocStatus] = useState<ValidationStatus>('idle');
  const [emailStatus, setEmailStatus] = useState<ValidationStatus>('idle');

  // Verificar duplicado de documento (busca en toda la empresa)
  const checkDocumentDuplicate = useCallback(async (value: string) => {
    if (!value || value.length < 3) {
      setDocStatus('idle');
      setDuplicateInOtherSite(null);
      setShowLinkToast(false);
      return;
    }
    if (isEditing && initialData?.documentNumber === value) {
      setDocStatus('valid');
      setDuplicateInOtherSite(null);
      setShowLinkToast(false);
      return;
    }

    setDocStatus('checking');
    try {
      // Buscar en toda la empresa
      const results = await patientsApi.searchCompany({ query: value, searchBy: 'document' });
      const match = results.find(r => r.documentNumber.toUpperCase() === value.toUpperCase());

      if (match) {
        if (match.isLinkedToCurrentSite && match.isActiveInCurrentSite) {
          // Ya existe y está activo en este centro
          setDocStatus('duplicate');
          setDuplicateInOtherSite(null);
          setShowLinkToast(false);
          toast.warning('Documento duplicado', 'Ya existe un paciente con este documento en este centro.');
        } else {
          // Existe en otro centro o está inactivo aquí - mostrar toast de vinculación
          setDocStatus('duplicate_other_site');
          setDuplicateInOtherSite(match);
          setShowLinkToast(true);
          setToastMedicalRecordNumber('');
        }
      } else {
        setDocStatus('valid');
        setDuplicateInOtherSite(null);
        setShowLinkToast(false);
      }
    } catch (error) {
      console.error('Error checking document:', error);
      setDocStatus('idle');
      setDuplicateInOtherSite(null);
      setShowLinkToast(false);
    }
  }, [isEditing, initialData?.documentNumber]);

  // Verificar duplicado de email (solo en centro actual)
  const checkEmailDuplicate = useCallback(async (value: string) => {
    if (!value || value.length < 3) {
      setEmailStatus('idle');
      return;
    }
    if (isEditing && initialData?.email === value) {
      setEmailStatus('valid');
      return;
    }

    setEmailStatus('checking');
    try {
      const results = await patientsApi.search({ query: value, searchBy: 'email' });
      const isDup = results.some(r => r.id !== initialData?.id);
      if (isDup) {
        setEmailStatus('duplicate');
        toast.warning('Email duplicado', 'Ya existe un paciente con este email.');
      } else {
        setEmailStatus('valid');
      }
    } catch {
      setEmailStatus('idle');
    }
  }, [isEditing, initialData?.id, initialData?.email]);

  useEffect(() => {
    checkDocumentDuplicate(debouncedDoc);
  }, [debouncedDoc, checkDocumentDuplicate]);

  useEffect(() => {
    checkEmailDuplicate(debouncedEmail);
  }, [debouncedEmail, checkEmailDuplicate]);

  const handlePhoneInput = (field: 'phonePrimary' | 'phoneSecondary') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(field, e.target.value.replace(/\D/g, ''), { shouldValidate: true });
  };

  // Vincular paciente existente
  const handleLinkPatient = async () => {
    if (!duplicateInOtherSite) return;

    setIsLinking(true);
    try {
      const result = await patientsApi.linkToSite({
        patientId: duplicateInOtherSite.id,
        medicalRecordNumber: toastMedicalRecordNumber.trim() || undefined
      });
      const mrnMsg = result.medicalRecordNumber ? ` con Nº Historia ${result.medicalRecordNumber}` : '';
      toast.success('Paciente vinculado', `${duplicateInOtherSite.fullName} ha sido vinculado a este centro${mrnMsg}.`);
      navigate(`/pacientes/${duplicateInOtherSite.id}`);
    } catch (err: any) {
      toast.error('Error al vincular', err.message);
    } finally {
      setIsLinking(false);
    }
  };

  // Descartar toast, borrar DNI y permitir crear nuevo
  const handleDismissLinkToast = () => {
    setShowLinkToast(false);
    setDuplicateInOtherSite(null);
    setToastMedicalRecordNumber('');
    // Borrar el DNI escrito
    setValue('documentNumber', '', { shouldValidate: false });
    // Resetear el estado de validación
    setDocStatus('idle');
  };

  const hasDuplicates = docStatus === 'duplicate' || emailStatus === 'duplicate';

  const handleFormSubmit = async (data: PatientFormData) => {
    if (hasDuplicates) {
      toast.error('No se puede guardar', 'Hay campos duplicados en este centro.');
      return;
    }
    await onSubmit(data);
  };

  const StatusIcon = ({ status }: { status: ValidationStatus }) => {
    if (status === 'checking') return <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />;
    if (status === 'valid') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (status === 'duplicate') return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (status === 'duplicate_other_site') return <Link2 className="h-4 w-4 text-blue-500" />;
    return null;
  };

  return (
    <>
      {/* Toast flotante para vincular paciente */}
      {showLinkToast && duplicateInOtherSite && (
        <LinkPatientToast
          patient={duplicateInOtherSite}
          onLink={handleLinkPatient}
          onDismiss={handleDismissLinkToast}
          isLinking={isLinking}
          medicalRecordNumber={toastMedicalRecordNumber}
          onMedicalRecordNumberChange={setToastMedicalRecordNumber}
        />
      )}

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Datos personales */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos personales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input type="text" {...register('firstName', { required: 'Obligatorio', minLength: { value: 2, message: 'Mínimo 2 caracteres' } })} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.firstName ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apellidos *</label>
              <input type="text" {...register('lastName', { required: 'Obligatorio', minLength: { value: 2, message: 'Mínimo 2 caracteres' } })} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.lastName ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo documento *</label>
              <select {...register('documentType')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nº documento *</label>
              <div className="relative">
                <input
                  type="text"
                  {...register('documentNumber', { required: 'Obligatorio', minLength: { value: 5, message: 'Mínimo 5 caracteres' } })}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 uppercase ${
                    errors.documentNumber || docStatus === 'duplicate'
                      ? 'border-red-300'
                      : docStatus === 'duplicate_other_site'
                        ? 'border-blue-300 ring-2 ring-blue-100'
                        : docStatus === 'valid'
                          ? 'border-green-300'
                          : 'border-gray-300'
                  }`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <StatusIcon status={docStatus} />
                </div>
              </div>
              {errors.documentNumber && <p className="mt-1 text-sm text-red-600">{errors.documentNumber.message}</p>}
              {docStatus === 'duplicate' && <p className="mt-1 text-sm text-red-600">Ya existe en este centro</p>}
              {docStatus === 'duplicate_other_site' && showLinkToast && (
                <p className="mt-1 text-sm text-blue-600 flex items-center gap-1">
                  <Link2 className="h-3 w-3" /> Paciente encontrado en otro centro
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha nacimiento *</label>
              <input type="date" {...register('birthDate', { required: 'Obligatorio' })} max={new Date().toISOString().split('T')[0]} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.birthDate ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.birthDate && <p className="mt-1 text-sm text-red-600">{errors.birthDate.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nº Historia</label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  {...register('medicalRecordNumber')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm uppercase"
                  placeholder="Ej: HC-12345"
                  onChange={(e) => setValue('medicalRecordNumber', e.target.value.toUpperCase())}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Número de historia en este centro (opcional)</p>
            </div>
          </div>
        </div>

        {/* Contacto */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contacto</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono principal *</label>
              <input type="tel" inputMode="numeric" {...register('phonePrimary', { required: 'Obligatorio', pattern: { value: /^[0-9]{9,15}$/, message: '9-15 dígitos' } })} onChange={handlePhoneInput('phonePrimary')} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.phonePrimary ? 'border-red-300' : 'border-gray-300'}`} placeholder="612345678" />
              {errors.phonePrimary && <p className="mt-1 text-sm text-red-600">{errors.phonePrimary.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono secundario</label>
              <input type="tel" inputMode="numeric" {...register('phoneSecondary')} onChange={handlePhoneInput('phoneSecondary')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Opcional" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <input type="email" {...register('email', { required: 'Obligatorio', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' } })} className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.email || emailStatus === 'duplicate' ? 'border-red-300' : emailStatus === 'valid' ? 'border-green-300' : 'border-gray-300'}`} />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3"><StatusIcon status={emailStatus} /></div>
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
              {emailStatus === 'duplicate' && <p className="mt-1 text-sm text-red-600">Ya existe un paciente con este email</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferencia contacto *</label>
              <select {...register('contactPref')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                {Object.entries(CONTACT_PREF_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Compañía de seguros *</label>
              <select
                {...register('insuranceCompanyId', { required: 'Selecciona una compañía de seguros' })}
                disabled={loadingInsurance}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.insuranceCompanyId ? 'border-red-300' : 'border-gray-300'} ${loadingInsurance ? 'bg-gray-100' : ''}`}
              >
                <option value="">-- Seleccionar compañía --</option>
                {insuranceCompanies.map((ic) => (
                  <option key={ic.id} value={ic.id}>{ic.name}</option>
                ))}
              </select>
              {errors.insuranceCompanyId && <p className="mt-1 text-sm text-red-600">{errors.insuranceCompanyId.message}</p>}
            </div>
          </div>
        </div>

        {/* Dirección */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Dirección <span className="text-sm font-normal text-gray-500">(opcional)</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Calle</label>
              <input type="text" {...register('addressStreet')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
              <input type="text" {...register('addressNumber')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Piso/Puerta</label>
              <input type="text" {...register('addressFloor')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código postal</label>
              <input type="text" {...register('postalCode')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
              <input type="text" {...register('city')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
              <input type="text" {...register('province')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">País</label>
              <input type="text" {...register('country')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        {/* Notas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notas internas</h2>
          <textarea {...register('adminNotes')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none" placeholder="Notas adicionales..." />
        </div>

        {/* Warning de duplicados locales */}
        {hasDuplicates && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-red-800">Campos duplicados</h4>
              <p className="text-sm text-red-700">Ya existe un paciente con estos datos en este centro.</p>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button type="button" onClick={onCancel} disabled={isLoading || isLinking} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading || hasDuplicates || isLinking}
            className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Guardando...</> : <><Save className="h-4 w-4" /> {isEditing ? 'Guardar cambios' : 'Crear paciente'}</>}
          </button>
        </div>
      </form>
    </>
  );
}
